import {
  API_BASE_DETAIL_TREATMENTS_URL,
  API_DETAIL_TREATMENTS_ALL,
  API_DETAIL_TREATMENTS_STATS,
  API_DETAIL_TREATMENTS_DETAILS,
  API_DETAIL_TREATMENTS_CREATE,
  API_DETAIL_TREATMENTS_UPDATE,
  API_DETAIL_TREATMENTS_DELETE,
  API_DETAIL_TREATMENTS_BY_PATIENT,
  API_DETAIL_TREATMENTS_BY_DOCTOR,        
  API_DETAIL_TREATMENTS_SUMMARY,
  API_DETAIL_TREATMENTS_CREATE_FROM_EXISTING,
  API_DETAIL_TREATMENTS_UPDATE_CONSENT,
  API_DETAIL_TREATMENTS_UPDATE_PROGRESS,
  API_DETAIL_TREATMENTS_UPDATE_DOCTOR,
  CONSENT_SUFFIX,
  PROGRESS_SUFFIX,
  DOCTOR_INFO_SUFFIX,
  SUMMARY_SUFFIX
} from '../../../helpers/urlHelper';

// ========================================================================
// 🔧 FUNCIONES HELPER PARA CONSTRUIR URLs COMPLETAS
// ========================================================================

/**
 * Construye la URL para obtener todos los tratamientos del sistema
 * @returns {string} URL completa
 */
export const buildGetAllDetailTreatmentsUrl = () => {
  return `${API_BASE_DETAIL_TREATMENTS_URL}${API_DETAIL_TREATMENTS_ALL}`;
};

/**
 * Construye la URL para obtener estadísticas de tratamientos
 * @returns {string} URL completa
 */
export const buildGetDetailTreatmentsStatsUrl = () => {
  return `${API_BASE_DETAIL_TREATMENTS_URL}${API_DETAIL_TREATMENTS_STATS}`;
};

/**
 * Construye la URL para obtener resumen de tratamientos por paciente
 * @param {string} patientId - ID del paciente
 * @returns {string} URL completa
 */
export const buildDetailTreatmentsSummaryUrl = (patientId) => {
  return `${API_BASE_DETAIL_TREATMENTS_URL}${API_DETAIL_TREATMENTS_SUMMARY}/${patientId}${SUMMARY_SUFFIX}`;
};

/**
 * Construye la URL para obtener tratamientos por paciente
 * @param {string} patientId - ID del paciente
 * @returns {string} URL completa
 */
export const buildDetailTreatmentsByPatientUrl = (patientId) => {
  return `${API_BASE_DETAIL_TREATMENTS_URL}${API_DETAIL_TREATMENTS_BY_PATIENT}/${patientId}`;
};

/**
 * Construye la URL para obtener tratamientos por doctor
 * @param {string} doctorId - ID del doctor
 * @returns {string} URL completa
 */
export const buildDetailTreatmentsByDoctorUrl = (doctorId) => {
  return `${API_BASE_DETAIL_TREATMENTS_URL}${API_DETAIL_TREATMENTS_BY_DOCTOR}/${doctorId}`;
};

/**
 * Construye la URL para crear desde tratamiento existente
 * @returns {string} URL completa
 */
export const buildCreateFromExistingUrl = () => {
  return `${API_BASE_DETAIL_TREATMENTS_URL}${API_DETAIL_TREATMENTS_CREATE_FROM_EXISTING}`;
};

/**
 * Construye la URL para crear tratamiento manual
 * @returns {string} URL completa
 */
export const buildCreateDetailTreatmentUrl = () => {
  return `${API_BASE_DETAIL_TREATMENTS_URL}${API_DETAIL_TREATMENTS_CREATE}`;
};

/**
 * Construye la URL para obtener un tratamiento específico
 * @param {string} treatmentId - ID del tratamiento
 * @returns {string} URL completa
 */
export const buildDetailTreatmentByIdUrl = (treatmentId) => {
  return `${API_BASE_DETAIL_TREATMENTS_URL}${API_DETAIL_TREATMENTS_DETAILS}/${treatmentId}`;
};

/**
 * Construye la URL para actualizar tratamiento
 * @param {string} treatmentId - ID del tratamiento
 * @returns {string} URL completa
 */
export const buildUpdateDetailTreatmentUrl = (treatmentId) => {
  return `${API_BASE_DETAIL_TREATMENTS_URL}${API_DETAIL_TREATMENTS_UPDATE}/${treatmentId}`;
};

/**
 * Construye la URL para eliminar tratamiento
 * @param {string} treatmentId - ID del tratamiento
 * @returns {string} URL completa
 */
export const buildDeleteDetailTreatmentUrl = (treatmentId) => {
  return `${API_BASE_DETAIL_TREATMENTS_URL}${API_DETAIL_TREATMENTS_DELETE}/${treatmentId}`;
};

/**
 * Construye la URL para actualizar consentimiento
 * @param {string} treatmentId - ID del tratamiento
 * @returns {string} URL completa
 */
export const buildUpdateConsentUrl = (treatmentId) => {
  return `${API_BASE_DETAIL_TREATMENTS_URL}${API_DETAIL_TREATMENTS_UPDATE_CONSENT}/${treatmentId}${CONSENT_SUFFIX}`;
};

/**
 * Construye la URL para actualizar progreso
 * @param {string} treatmentId - ID del tratamiento
 * @returns {string} URL completa
 */
export const buildUpdateProgressUrl = (treatmentId) => {
  return `${API_BASE_DETAIL_TREATMENTS_URL}${API_DETAIL_TREATMENTS_UPDATE_PROGRESS}/${treatmentId}${PROGRESS_SUFFIX}`;
};

/**
 * Construye la URL para actualizar información del doctor
 * @param {string} treatmentId - ID del tratamiento
 * @returns {string} URL completa
 */
export const buildUpdateDoctorInfoUrl = (treatmentId) => {
  return `${API_BASE_DETAIL_TREATMENTS_URL}${API_DETAIL_TREATMENTS_UPDATE_DOCTOR}/${treatmentId}${DOCTOR_INFO_SUFFIX}`;
};

// ========================================================================
// 🔄 FUNCIONES DE SERVICIO PARA PETICIONES HTTP - CONSULTAS GENERALES
// ========================================================================

/**
 * Obtiene todos los tratamientos del sistema con paginación y filtros
 * @param {Object} params - Parámetros de consulta (page, limit, status, sort_by, sort_order)
 * @returns {Promise} Respuesta de la API
 */
export const getAllDetailTreatments = async (params = {}) => {
  try {
    const {
      page = 1,
      limit = 10,
      status,
      sort_by = 'treatment_info.last_update',
      sort_order = 'desc'
    } = params;

    // Construir query parameters
    const queryParams = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
      sort_by,
      sort_order
    });

    // Agregar filtro de status si se proporciona
    if (status) {
      queryParams.append('status', status);
    }

    const url = `${buildGetAllDetailTreatmentsUrl()}?${queryParams.toString()}`;
    
    console.log('Fetching all detail treatments with params:', params);

    const response = await fetch(url);
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(`HTTP error! status: ${response.status}, message: ${errorData.error || 'Unknown error'}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error fetching all detail treatments:', error);
    throw error;
  }
};

/**
 * Obtiene estadísticas generales de los tratamientos del sistema
 * @returns {Promise} Respuesta de la API con estadísticas
 */
export const getDetailTreatmentsStats = async () => {
  try {
    console.log('Fetching detail treatments statistics');

    const response = await fetch(buildGetDetailTreatmentsStatsUrl());
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(`HTTP error! status: ${response.status}, message: ${errorData.error || 'Unknown error'}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error fetching detail treatments statistics:', error);
    throw error;
  }
};

// ========================================================================
// 🔄 FUNCIONES DE SERVICIO - CONSULTAS POR PACIENTE
// ========================================================================

/**
 * Obtiene el resumen de tratamientos de un paciente
 * @param {string} patientId - ID del paciente
 * @returns {Promise} Respuesta de la API
 */
export const getDetailTreatmentsSummary = async (patientId) => {
  try {
    const response = await fetch(buildDetailTreatmentsSummaryUrl(patientId));
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error('Error fetching treatments summary:', error);
    throw error;
  }
};

/**
 * Obtiene todos los tratamientos detallados de un paciente
 * @param {string} patientId - ID del paciente
 * @returns {Promise} Respuesta de la API
 */
export const getDetailTreatmentsByPatient = async (patientId) => {
  try {
    const response = await fetch(buildDetailTreatmentsByPatientUrl(patientId));
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error('Error fetching detail treatments by patient:', error);
    throw error;
  }
};

// ========================================================================
// 🔄 FUNCIONES DE SERVICIO - CONSULTAS POR DOCTOR
// ========================================================================

/**
 * Obtiene todos los tratamientos asignados a un doctor específico
 * @param {string} doctorId - ID del doctor
 * @param {Object} params - Parámetros de consulta (page, limit, status)
 * @returns {Promise} Respuesta de la API
 */
export const getDetailTreatmentsByDoctor = async (doctorId, params = {}) => {
  try {
    const {
      page = 1,
      limit = 10,
      status
    } = params;

    // Construir query parameters
    const queryParams = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString()
    });

    // Agregar filtro de status si se proporciona
    if (status) {
      queryParams.append('status', status);
    }

    const url = `${buildDetailTreatmentsByDoctorUrl(doctorId)}?${queryParams.toString()}`;
    
    console.log('Fetching detail treatments by doctor:', doctorId, 'with params:', params);

    const response = await fetch(url);
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(`HTTP error! status: ${response.status}, message: ${errorData.error || 'Unknown error'}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error fetching detail treatments by doctor:', error);
    throw error;
  }
};

// ========================================================================
// 🔄 FUNCIONES DE SERVICIO - CONSULTAS INDIVIDUALES
// ========================================================================

/**
 * Obtiene un tratamiento detallado específico
 * @param {string} treatmentId - ID del tratamiento
 * @returns {Promise} Respuesta de la API
 */
export const getDetailTreatmentById = async (treatmentId) => {
  try {
    const response = await fetch(buildDetailTreatmentByIdUrl(treatmentId));
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error('Error fetching detail treatment by ID:', error);
    throw error;
  }
};

// ========================================================================
// 🔄 FUNCIONES DE SERVICIO - CREACIÓN DE TRATAMIENTOS
// ========================================================================

/**
 * Crea un nuevo tratamiento detallado desde un TreatmentManagement existente
 * @param {Object} treatmentData - Datos del tratamiento
 * @returns {Promise} Respuesta de la API
 */
export const createDetailTreatmentFromExisting = async (treatmentData) => {
  try {
    // Validaciones básicas
    if (!treatmentData.treatmentManagementId) {
      throw new Error('treatmentManagementId is required');
    }
    if (!treatmentData.patient_id) {
      throw new Error('patient_id is required');
    }
    if (!treatmentData.doctor_id) {
      throw new Error('doctor_id is required');
    }

    const requestBody = {
      treatmentManagementId: treatmentData.treatmentManagementId,
      patient_id: treatmentData.patient_id,
      doctor_id: treatmentData.doctor_id,
      
      doctor_info: {
        doctor_name: treatmentData.doctor_info?.doctor_name || "Doctor Name Pending",
        doctor_email: treatmentData.doctor_info?.doctor_email || "",
        specialty: treatmentData.doctor_info?.specialty || "",
        license_number: treatmentData.doctor_info?.license_number || ""
      },
      
      treatment_info: {
        start_date: treatmentData.treatment_info?.start_date || null,
        estimated_end_date: treatmentData.treatment_info?.estimated_end_date || null,
        status: treatmentData.treatment_info?.status || 'pending',
        progress_percentage: treatmentData.treatment_info?.progress_percentage || 0
      },
      
      consent_status: {
        is_required: treatmentData.consent_status?.is_required ?? true,
        is_signed: treatmentData.consent_status?.is_signed ?? false,
        pending_signature: treatmentData.consent_status?.pending_signature ?? true
      }
    };

    console.log('Creating detail treatment from existing with body:', requestBody);

    const response = await fetch(buildCreateFromExistingUrl(), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody),
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(`HTTP error! status: ${response.status}, message: ${errorData.error || 'Unknown error'}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error creating detail treatment from existing:', error);
    throw error;
  }
};

/**
 * Crea un tratamiento detallado manualmente (estructura completa)
 * @param {Object} treatmentData - Datos completos del tratamiento
 * @returns {Promise} Respuesta de la API
 */
export const createDetailTreatmentManual = async (treatmentData) => {
  try {
    const response = await fetch(buildCreateDetailTreatmentUrl(), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(treatmentData),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(`HTTP error! status: ${response.status}, message: ${errorData.error || 'Unknown error'}`);
    }
    return await response.json();
  } catch (error) {
    console.error('Error creating detail treatment manually:', error);
    throw error;
  }
};

// ========================================================================
// 🔄 FUNCIONES DE SERVICIO - ACTUALIZACIÓN DE TRATAMIENTOS
// ========================================================================

/**
 * Actualiza un tratamiento detallado completo
 * @param {string} treatmentId - ID del tratamiento
 * @param {Object} treatmentData - Datos actualizados
 * @returns {Promise} Respuesta de la API
 */
export const updateDetailTreatment = async (treatmentId, treatmentData) => {
  try {
    const response = await fetch(buildUpdateDetailTreatmentUrl(treatmentId), {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(treatmentData),
    });
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error('Error updating detail treatment:', error);
    throw error;
  }
};

/**
 * Actualiza el consentimiento de un tratamiento
 * @param {string} treatmentId - ID del tratamiento
 * @param {Object} consentData - Datos del consentimiento
 * @returns {Promise} Respuesta de la API
 */
export const updateTreatmentConsent = async (treatmentId, consentData) => {
  try {
    const response = await fetch(buildUpdateConsentUrl(treatmentId), {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(consentData),
    });
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error('Error updating treatment consent:', error);
    throw error;
  }
};

/**
 * Actualiza el progreso de un tratamiento
 * @param {string} treatmentId - ID del tratamiento
 * @param {Object} progressData - Datos del progreso
 * @returns {Promise} Respuesta de la API
 */
export const updateTreatmentProgress = async (treatmentId, progressData) => {
  try {
    const response = await fetch(buildUpdateProgressUrl(treatmentId), {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(progressData),
    });
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error('Error updating treatment progress:', error);
    throw error;
  }
};

/**
 * Actualiza la información del doctor en un tratamiento específico
 * @param {string} treatmentId - ID del tratamiento
 * @param {Object} doctorInfo - Información del doctor a actualizar
 * @returns {Promise} Respuesta de la API
 */
export const updateDoctorInfo = async (treatmentId, doctorInfo) => {
  try {
    const requestBody = {
      doctor_info: {
        doctor_name: doctorInfo.doctor_name || "",
        doctor_email: doctorInfo.doctor_email || "",
        specialty: doctorInfo.specialty || "",
        license_number: doctorInfo.license_number || ""
      }
    };

    console.log('Updating doctor info for treatment:', treatmentId, 'with data:', requestBody);

    const response = await fetch(buildUpdateDoctorInfoUrl(treatmentId), {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody),
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(`HTTP error! status: ${response.status}, message: ${errorData.error || 'Unknown error'}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error updating doctor info:', error);
    throw error;
  }
};

// ========================================================================
// 🔄 FUNCIONES DE SERVICIO - ELIMINACIÓN
// ========================================================================

/**
 * Elimina un tratamiento detallado
 * @param {string} treatmentId - ID del tratamiento
 * @returns {Promise} Respuesta de la API
 */
export const deleteDetailTreatment = async (treatmentId) => {
  try {
    const response = await fetch(buildDeleteDetailTreatmentUrl(treatmentId), {
      method: 'DELETE',
    });
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error('Error deleting detail treatment:', error);
    throw error;
  }
};

// ========================================================================
// 🔧 FUNCIONES DE UTILIDAD Y VALIDACIÓN
// ========================================================================

/**
 * Función helper para validar ObjectIds en el frontend
 * @param {string} id - ID a validar
 * @param {string} fieldName - Nombre del campo para el error
 * @returns {string|null} Error message or null if valid
 */
export const validateObjectId = (id, fieldName) => {
  if (!id) return `${fieldName} is required`;
  if (typeof id !== 'string') return `${fieldName} must be a string`;
  if (id.length !== 24) return `${fieldName} must be 24 characters long`;
  if (!/^[0-9a-fA-F]{24}$/.test(id)) return `${fieldName} must be a valid ObjectId`;
  return null;
};

/**
 * Función para pre-validar datos antes de enviar
 * @param {Object} treatmentData - Datos del tratamiento a validar
 * @returns {Array} Array de errores encontrados
 */
export const validateTreatmentData = (treatmentData) => {
  const errors = [];
  
  const patientIdError = validateObjectId(treatmentData.patient_id, 'Patient ID');
  if (patientIdError) errors.push(patientIdError);
  
  const doctorIdError = validateObjectId(treatmentData.doctor_id, 'Doctor ID');
  if (doctorIdError) errors.push(doctorIdError);
  
  if (treatmentData.treatmentManagementId) {
    const templateIdError = validateObjectId(treatmentData.treatmentManagementId, 'Treatment Template ID');
    if (templateIdError) errors.push(templateIdError);
  }
  
  if (treatmentData.treatment_info?.start_date) {
    const startDate = new Date(treatmentData.treatment_info.start_date);
    if (isNaN(startDate.getTime())) {
      errors.push('Start date is invalid');
    }
  }
  
  return errors;
};

/**
 * Función helper para crear query params
 * @param {Object} params - Parámetros a convertir
 * @returns {string} Query string
 */
export const buildQueryParams = (params) => {
  const searchParams = new URLSearchParams();
  
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      searchParams.append(key, value.toString());
    }
  });
  
  return searchParams.toString();
};

// ========================================================================
// 🔄 FUNCIONES LEGACY PARA COMPATIBILIDAD
// ========================================================================

/**
 * Función legacy mantenida para compatibilidad hacia atrás
 * @deprecated Use createDetailTreatmentFromExisting instead
 */
export const createDetailTreatment = async (treatmentData) => {
  console.warn('createDetailTreatment is deprecated. Use createDetailTreatmentFromExisting instead.');
  return createDetailTreatmentFromExisting(treatmentData);
};

/**
 * Función legacy mantenida para compatibilidad hacia atrás  
 * @deprecated Use createDetailTreatmentFromExisting instead
 */
export const createFromExistingTreatment = async (existingTreatmentData) => {
  console.warn('createFromExistingTreatment is deprecated. Use createDetailTreatmentFromExisting instead.');
  return createDetailTreatmentFromExisting(existingTreatmentData);
};

/**
 * Función legacy con mejor manejo de errores
 * @deprecated Use createDetailTreatmentFromExisting instead
 */
export const createDetailTreatmentWithDoctor = async (treatmentData) => {
  console.warn('createDetailTreatmentWithDoctor is deprecated. Use createDetailTreatmentFromExisting instead.');
  return createDetailTreatmentFromExisting(treatmentData);
};